import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/client';
import {
  Plus,
  RefreshCw,
  Eye,
  Upload,
  CheckCircle2,
  User,
  Briefcase,
  Mail,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';
import FileUpload from '../../components/dashboard/FileUpload';
import { allCountries, civilities } from '../../data/quoteFormOptions';
import { getRegionOptions, getVilleOptions } from '../../data/geoData';
import '../../components/dashboard/DashForm.css';
import './AdminAddVerifierPage.css';
import './AdminHome.css';
import '../AuthPage.css';
import DashFormStepper from '../../components/dashboard/DashFormStepper';

export const LAWYER_SPECIALTIES = [
  'Juridique', 'Administratif', 'Commercial', 'Contrats',
  'Droit des affaires', 'Droit civil', 'Droit international', 'Autre',
];

export const NOTARY_SPECIALTIES = [
  'Actes notariés', 'Contrats', 'Immobilier', 'Successions',
  'Actes de sociétés', 'Documents officiels', 'Documents administratifs', 'Autre',
];

export const LAWYER_TYPES = [
  'Traduction juridique', 'Documents juridiques', 'Contrats',
  'Documents administratifs', 'Documents commerciaux', 'Autre',
];

export const NOTARY_TYPES = [
  'Vérification de traduction', 'Vérification de documents', 'Vérification de contrats',
  'Validation notariale', 'Vérification d\'actes officiels', 'Autre',
];

export const WORK_LANGUAGES = ['Arabe', 'Français', 'Anglais', 'Espagnol', 'Autre'];
export const LANGUAGE_CODE = {
  Arabe: 'ARABE',
  Français: 'FRANCAIS',
  Anglais: 'ANGLAIS',
  Espagnol: 'ESPAGNOL',
  Autre: 'AUTRE',
};

export const INTERVENTION = ['remote', 'presential', 'both'];

export const INTERVENTION_LABELS = {
  remote: 'À distance',
  presential: 'Sur site',
  both: 'Les deux',
};

export const AVAILABILITY = ['available', 'busy', 'unavailable'];

export const AVAILABILITY_LABELS = {
  available: 'Disponible',
  busy: 'Partiellement disponible',
  unavailable: 'Indisponible',
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Actif' },
  { value: 'inactive', label: 'Inactif' },
];

function makeTempPassword() {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%';
  const array = new Uint32Array(12);
  window.crypto.getRandomValues(array);
  return Array.from(array, (x) => chars[x % chars.length]).join('');
}

export default function AdminAddVerifierPage({ onClose, onSaved }) {
  const navigate = useNavigate();
  const isModal = Boolean(onClose);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState('lawyer');
  const [status, setStatus] = useState('active');
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    email: '',
    password: makeTempPassword(),
    name: '',
    phone: '',
    city: 'Tunis',
    region: '',
    country: 'TN',
    verifierProfile: {
      civility: '',
      firstName: '',
      lastName: '',
      professionalEmail: '',
      phone: '',
      country: 'TN',
      city: 'Tunis',
      region: '',
      firm: '',
      professionalNumber: '',
      professionalBody: '',
      specialties: [],
      verificationTypes: [],
      languages: [],
      experienceYears: '',
      interventionMode: 'both',
      availability: 'available',
      documents: [],
    },
  });

  const vp = form.verifierProfile;

  // Cascade Pays → Ville → Région (geoData : gouvernorats tunisiens + country-state-city)
  const villeOptions = useMemo(() => getVilleOptions(vp.country), [vp.country]);
  const regionOptions = useMemo(
    () => getRegionOptions(vp.country, vp.city),
    [vp.country, vp.city]
  );
  const regionRequired = regionOptions.length > 0;

  useEffect(() => {
    const full = [vp.firstName, vp.lastName].filter(Boolean).join(' ').trim();
    if (full && !form.name) setForm((prev) => ({ ...prev, name: full }));
  }, [vp.firstName, vp.lastName, form.name]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setProfile = (key, value) =>
    setForm((prev) => ({ ...prev, verifierProfile: { ...prev.verifierProfile, [key]: value } }));

  const selectSpecialty = (opt) =>
    setForm((prev) => ({ ...prev, verifierProfile: { ...prev.verifierProfile, specialties: [opt] } }));

  const selectType = (opt) =>
    setForm((prev) => ({ ...prev, verifierProfile: { ...prev.verifierProfile, verificationTypes: [opt] } }));

  const toggleLanguage = (opt) =>
    setForm((prev) => {
      const code = LANGUAGE_CODE[opt];
      const exists = prev.verifierProfile.languages.some((l) => l.language === code);
      const next = exists
        ? prev.verifierProfile.languages.filter((l) => l.language !== code)
        : [...prev.verifierProfile.languages, { language: code, level: 'C2' }];
      return { ...prev, verifierProfile: { ...prev.verifierProfile, languages: next } };
    });

  const generatePassword = () => {
    setField('password', makeTempPassword());
    setShowPassword(true);
  };

  const validate = () => {
    setError('');
    if (!vp.civility || !vp.firstName || !vp.lastName) return 'La civilité, le prénom et le nom sont obligatoires.';
    if (!vp.professionalEmail) return "L'email professionnel est obligatoire.";
    if (!vp.phone) return 'Le téléphone professionnel est obligatoire.';
    if (!vp.country) return 'Le pays est obligatoire.';
    if (!vp.city) return 'La ville est obligatoire.';
    if (regionRequired && !vp.region) return 'La région est obligatoire.';
    if (!vp.experienceYears || Number(vp.experienceYears) < 0) return "Les années d'expérience sont obligatoires.";
    if (!vp.languages.length) return 'Au moins une langue de travail est requise.';
    if (!vp.firm) return role === 'lawyer' ? 'Le barreau est obligatoire.' : "L'étude / office notarial est obligatoire.";
    if (!vp.professionalNumber) return 'Le numéro professionnel est obligatoire.';
    if (!vp.specialties.length) return role === 'lawyer' ? 'Au moins un domaine de vérification est requis.' : "Au moins un domaine d'intervention est requis.";
    if (!vp.verificationTypes.length) return role === 'lawyer' ? 'Au moins un type de vérification est requis.' : 'Au moins un type\'intervention est requis.';
    if (!form.email) return "L'email de connexion est obligatoire.";
    if (!form.password) return 'Le mot de passe temporaire est obligatoire.';
    if (!vp.documents.length) return 'Un justificatif professionnel est obligatoire.';
    return '';
  };

  const validateStep = (s) => {
    if (s === 1) {
      if (!vp.civility || !vp.firstName || !vp.lastName) return 'La civilité, le prénom et le nom sont obligatoires.';
      if (!vp.professionalEmail) return "L'email professionnel est obligatoire.";
      if (!vp.phone) return 'Le téléphone professionnel est obligatoire.';
      if (!vp.country) return 'Le pays est obligatoire.';
      if (!vp.city) return 'La ville est obligatoire.';
      if (regionRequired && !vp.region) return 'La région est obligatoire.';
      if (!vp.experienceYears || Number(vp.experienceYears) < 0) return "Les années d'expérience sont obligatoires.";
      if (!vp.languages.length) return 'Au moins une langue de travail est requise.';
    }
    if (s === 2) {
      if (!vp.firm) return role === 'lawyer' ? 'Le barreau est obligatoire.' : "L'étude / office notarial est obligatoire.";
      if (!vp.professionalNumber) return 'Le numéro professionnel est obligatoire.';
      if (!vp.specialties.length) return role === 'lawyer' ? 'Au moins un domaine de vérification est requis.' : "Au moins un domaine d'intervention est requis.";
      if (!vp.verificationTypes.length) return role === 'lawyer' ? 'Au moins un type de vérification est requis.' : "Au moins un type d'intervention est requis.";
      if (!form.email) return "L'email de connexion est obligatoire.";
      if (!form.password) return 'Le mot de passe temporaire est obligatoire.';
    }
    if (s === 3) {
      if (!vp.documents.length) return 'Un justificatif professionnel est obligatoire.';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msg = validate();
    if (msg) return setError(msg);

    setError('');
    const displayName = form.name.trim() || `${vp.firstName.trim()} ${vp.lastName.trim()}`.trim();
    const isActive = status !== 'inactive';
    const payload = {
      ...form,
      name: displayName,
      email: form.email.trim(),
      phone: vp.phone.trim(),
      city: vp.city.trim(),
      region: vp.region.trim(),
      country: vp.country.trim(),
      role,
      isActive,
      verifierProfile: {
        ...vp,
        type: role,
        firstName: vp.firstName.trim(),
        lastName: vp.lastName.trim(),
        experienceYears: Number(vp.experienceYears) || 0,
        availability: status === 'active' ? vp.availability : 'unavailable',
      },
    };

    try {
      await adminApi.createVerifier(payload);
      setSaved(true);
      setTimeout(() => {
        if (onSaved) onSaved();
        else navigate('/admin/verifiers');
      }, 1200);
    } catch (err) {
      setError(err.data?.error || 'Erreur lors de la création.');
    }
  };

  return (
    <>
      {saved && (
        <div className="auth-toast" role="status">
          <CheckCircle2 size={18} />
          <span>Vérificateur créé avec succès.</span>
        </div>
      )}

      <div className="auth-page" style={isModal ? { minHeight: 'auto', padding: 0, background: '#fff' } : undefined}>
        <div
          className="auth-shell auth-shell--translator auth-shell--wide"
          style={isModal ? { display: 'block', minHeight: 'auto', boxShadow: 'none', borderRadius: 0 } : undefined}
        >
          <div className="auth-form-panel" style={{ maxWidth: '100%', gridColumn: '1 / -1' }}>
            <form className="dform" onSubmit={(e) => e.preventDefault()}>

              <div className="dform-field" style={{ textAlign: 'center', marginBottom: 8 }}>
                <label style={{ justifyContent: 'center' }}>Type de professionnel</label>
                <div className="dform-radio-group" style={{ gridTemplateColumns: 'repeat(2, minmax(120px, 1fr))', maxWidth: 280, margin: '8px auto 0' }}>
                  {[
                    { value: 'lawyer', label: 'Avocat' },
                    { value: 'notary', label: 'Notaire' },
                  ].map((opt) => (
                    <label key={opt.value} className="dform-radio">
                      <input
                        type="radio"
                        name="role"
                        value={opt.value}
                        checked={role === opt.value}
                        onChange={(e) => setRole(e.target.value)}
                        required
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              <DashFormStepper
                steps={['Informations', 'Professionnel', 'Justificatif']}
                current={step}
                onSelect={setStep}
              />

              <div style={{ marginTop: 24 }}>
                {step === 1 && (
                  <div className="dform-row">
                    <section className="dform-section">
                      <header className="dform-section-head">
                        <User size={17} />
                        <h4>Informations communes</h4>
                      </header>
                      <div className="dform-grid dform-grid--2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                        <div className="dform-field">
                          <label>Civilité <span className="dform-req">*</span></label>
                          <select value={vp.civility} required onChange={(e) => setProfile('civility', e.target.value)}>
                            <option value="">Sélectionner</option>
                            {civilities.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                        <div className="dform-field">
                          <label>Prénom <span className="dform-req">*</span></label>
                          <input required value={vp.firstName} onChange={(e) => setProfile('firstName', e.target.value)} placeholder="Ahmed" />
                        </div>
                        <div className="dform-field">
                          <label>Nom <span className="dform-req">*</span></label>
                          <input required value={vp.lastName} onChange={(e) => setProfile('lastName', e.target.value)} placeholder="Ben Ali" />
                        </div>
                        <div className="dform-field">
                          <label>Email professionnel <span className="dform-req">*</span></label>
                          <input type="email" required value={vp.professionalEmail} onChange={(e) => setProfile('professionalEmail', e.target.value)} />
                        </div>
                        <div className="dform-field">
                          <label>Téléphone professionnel <span className="dform-req">*</span></label>
                          <input required value={vp.phone} onChange={(e) => setProfile('phone', e.target.value)} />
                        </div>
                        <div className="dform-field">
                          <label>Pays <span className="dform-req">*</span></label>
                          <select
                            required
                            value={vp.country}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                verifierProfile: { ...prev.verifierProfile, country: e.target.value, city: '', region: '' },
                              }))
                            }
                          >
                            <option value="">Sélectionnez un pays</option>
                            {allCountries.map(({ code, name }) => (
                              <option key={code} value={code}>{name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="dform-field">
                          <label>Ville <span className="dform-req">*</span></label>
                          {villeOptions.length ? (
                            <select
                              required
                              value={vp.city}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  verifierProfile: { ...prev.verifierProfile, city: e.target.value, region: '' },
                                }))
                              }
                            >
                              <option value="">Sélectionnez une ville</option>
                              {villeOptions.map((v) => (
                                <option key={v.stateCode || v.name} value={v.name}>{v.name}</option>
                              ))}
                              {vp.city && !villeOptions.some((v) => v.name === vp.city) && (
                                <option value={vp.city}>{vp.city}</option>
                              )}
                            </select>
                          ) : (
                            <input required value={vp.city} onChange={(e) => setProfile('city', e.target.value)} />
                          )}
                        </div>
                        <div className="dform-field">
                          <label>Région {regionRequired && <span className="dform-req">*</span>}</label>
                          {regionRequired ? (
                            <select required value={vp.region} onChange={(e) => setProfile('region', e.target.value)}>
                              <option value="">Sélectionnez une région</option>
                              {regionOptions.map((r) => (
                                <option key={r} value={r}>{r}</option>
                              ))}
                              {vp.region && !regionOptions.includes(vp.region) && (
                                <option value={vp.region}>{vp.region}</option>
                              )}
                            </select>
                          ) : (
                            <input
                              value={vp.region}
                              onChange={(e) => setProfile('region', e.target.value)}
                              placeholder="Ex. La Marsa"
                              disabled={villeOptions.length > 0 && !vp.city}
                            />
                          )}
                        </div>
                        <div className="dform-field">
                          <label>Années d'expérience <span className="dform-req">*</span></label>
                          <input type="number" min={0} required value={vp.experienceYears} onChange={(e) => setProfile('experienceYears', e.target.value)} />
                        </div>
                        <div className="dform-field">
                          <label>Langue de travail <span className="dform-req">*</span></label>
                          <div className="dform-checkbox-scroll" style={{ gridTemplateColumns: '1fr', gap: 0, padding: 0, border: 'none', background: 'transparent' }}>
                            {WORK_LANGUAGES.map((opt) => (
                              <label key={opt} className="dform-checkbox" style={{ padding: '2px 0', border: 'none', background: 'transparent', gap: 6 }}>
                                <input
                                  type="checkbox"
                                  checked={vp.languages.some((l) => l.language === LANGUAGE_CODE[opt])}
                                  onChange={() => toggleLanguage(opt)}
                                />
                                {opt}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>



                      <div className="dform-grid dform-grid--2" style={{ marginTop: 12 }}>
                        <div className="dform-field">
                          <label>Disponibilité <span className="dform-req">*</span></label>
                          <div className="dform-radio-group">
                            {AVAILABILITY.map((a) => (
                              <label key={a} className="dform-radio">
                                <input
                                  type="radio"
                                  name="availability"
                                  value={a}
                                  checked={vp.availability === a}
                                  onChange={(e) => setProfile('availability', e.target.value)}
                                />
                                {AVAILABILITY_LABELS[a]}
                              </label>
                            ))}
                          </div>
                        </div>
                        <div className="dform-field">
                          <label>Mode d'intervention <span className="dform-req">*</span></label>
                          <div className="dform-radio-group">
                            {INTERVENTION.map((m) => (
                              <label key={m} className="dform-radio">
                                <input
                                  type="radio"
                                  name="interventionMode"
                                  value={m}
                                  checked={vp.interventionMode === m}
                                  onChange={(e) => setProfile('interventionMode', e.target.value)}
                                />
                                {INTERVENTION_LABELS[m]}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                )}

                {step === 2 && (
                  <div className="dform-row" style={{ display: 'flex' }}>
                    <section className="dform-section" style={{ flex: 1 }}>
                      <header className="dform-section-head">
                        <Briefcase size={17} />
                        <h4>Informations professionnelles</h4>
                      </header>
                      <div className="dform-grid dform-grid--2">
                        <div className="dform-field">
                          <label>{role === 'lawyer' ? 'Barreau' : "Étude / Office notarial"} <span className="dform-req">*</span></label>
                          <input required value={vp.firm} onChange={(e) => setProfile('firm', e.target.value)} />
                        </div>
                        <div className="dform-field">
                          <label>Numéro professionnel <span className="dform-req">*</span></label>
                          <input required value={vp.professionalNumber} onChange={(e) => setProfile('professionalNumber', e.target.value)} />
                        </div>
                      </div>

                      <div className="dform-field dform-wide" style={{ marginTop: 12 }}>
                        <label>{role === 'lawyer' ? 'Domaines de vérification' : "Domaines d'intervention"} <span className="dform-req">*</span></label>
                        <div className="dform-radio-group">
                          {(role === 'lawyer' ? LAWYER_SPECIALTIES : NOTARY_SPECIALTIES).map((opt) => (
                            <label key={opt} className="dform-radio">
                              <input
                                type="radio"
                                name="specialty"
                                value={opt}
                                checked={vp.specialties[0] === opt}
                                onChange={(e) => selectSpecialty(e.target.value)}
                              />
                              {opt}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="dform-field dform-wide" style={{ marginTop: 12 }}>
                        <label>{role === 'lawyer' ? 'Types de vérification' : "Types d'intervention"} <span className="dform-req">*</span></label>
                        <div className="dform-radio-group">
                          {(role === 'lawyer' ? LAWYER_TYPES : NOTARY_TYPES).map((opt) => (
                            <label key={opt} className="dform-radio">
                              <input
                                type="radio"
                                name="verificationType"
                                value={opt}
                                checked={vp.verificationTypes[0] === opt}
                                onChange={(e) => selectType(e.target.value)}
                              />
                              {opt}
                            </label>
                          ))}
                        </div>
                      </div>
                    </section>

                    <div style={{ width: 1, background: 'var(--gray-200)', margin: '0 8px' }} />

                    <section className="dform-section" style={{ flex: 1 }}>
                      <header className="dform-section-head">
                        <Mail size={17} />
                        <h4>Compte plateforme</h4>
                      </header>
                      <div className="dform-grid dform-grid--2">
                        <div className="dform-field dform-wide">
                          <label>Email de connexion <span className="dform-req">*</span></label>
                          <input type="email" required value={form.email} onChange={(e) => setField('email', e.target.value)} />
                        </div>
                        <div className="dform-field dform-wide">
                          <label>Mot de passe temporaire <span className="dform-req">*</span></label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div className="dform-pwd" style={{ flex: 1 }}>
                              <input
                                required
                                type={showPassword ? 'text' : 'password'}
                                value={form.password}
                                onChange={(e) => setField('password', e.target.value)}
                              />
                              <button type="button" className="dform-pwd-toggle" onClick={() => setShowPassword((v) => !v)} title="Afficher / masquer">
                                <Eye size={15} />
                              </button>
                            </div>
                            <button type="button" className="btn btn-outline" onClick={generatePassword}>
                              <RefreshCw size={15} /> Générer
                            </button>
                          </div>
                        </div>
                        <div className="dform-field">
                          <label>Statut du compte</label>
                          <select value={status} onChange={(e) => setStatus(e.target.value)}>
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </section>
                  </div>
                )}

                {step === 3 && (
                  <div className="dform-row">
                    <section className="dform-section">
                      <header className="dform-section-head">
                        <Upload size={17} />
                        <h4>Justificatif professionnel</h4>
                      </header>
                      <div className="dform-field dform-wide">
                        <label>Pièce justificative <span className="dform-req">*</span></label>
                        <FileUpload
                          value={vp.documents[0] || ''}
                          onChange={(url) => setProfile('documents', url ? [url] : [])}
                          accept=".pdf,.doc,.docx,image/*"
                          uploadApi={adminApi.uploadFile}
                        />
                        {!vp.documents.length && (
                          <p className="dform-hint">Veuillez téléverser au moins un document.</p>
                        )}
                      </div>
                    </section>
                  </div>
                )}
              </div>

              {error && (
                <div className="auth-error" role="alert" style={{ marginTop: 16 }}>
                  {error}
                </div>
              )}

              <footer className="dform-footer" style={{ marginTop: 24 }}>
                <span className="dform-progress">Étape {step} / 3</span>
                <span className="dform-spacer" />
                {!isModal && (
                  <button type="button" className="btn btn-outline" onClick={() => navigate('/admin/verifiers')}>
                    <ArrowLeft size={15} /> Retour
                  </button>
                )}
                {step > 1 && (
                  <button type="button" className="btn btn-outline" onClick={() => setStep(step - 1)}>
                    <ArrowLeft size={15} /> Précédent
                  </button>
                )}
                {step < 3 && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      const msg = validateStep(step);
                      if (msg) return setError(msg);
                      setError('');
                      setStep(step + 1);
                    }}
                  >
                    Suivant <ArrowRight size={15} />
                  </button>
                )}
                {step === 3 && (
                  <button type="button" className="btn btn-primary" disabled={saved} onClick={handleSubmit}>
                    <Plus size={15} /> Ajouter le vérificateur
                  </button>
                )}
              </footer>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
